import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const FAQ = () => {
  const { t, i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // ⚡ FAQ en Français ⚡
  const faqsFr = [
    {
      question: "Est-ce que vos produits sont artisanaux et locaux ?",
      answer: `Nous sommes transformateurs et achetons nos coings chez des producteurs de proximité dans le Vaucluse et en Drôme provençale, qui cultivent des coings de qualité. Cependant nous disposons de cognassiers qui nous permettent de fabriquer une partie de nos produits sucrés.`,
      author: "Répondu par Guimety Corinne",
    },
    {
      question: "Disposez-vous d'un point de vente sur place ?",
      answer: `Non, nos produits sont distribués dans des épiceries fines, des caveaux, des restaurants et des supermarchés. Vous retrouverez la liste complète dans la page "Points de Vente" du site.`,
      author: "Répondu par l'équipe Au Coing Du Ventoux",
    },
    {
      question: "Proposez-vous la vente en ligne ?",
      answer: `Pour l'instant, l'utilisation de notre site est limitée à la présentation de nos produits et de notre entreprise. Un formulaire de commande est disponible après avoir rempli votre panier et validé votre email. Dans un futur proche, nous envisageons de mettre en place un système de vente en ligne.`,
      author: "Répondu par l'équipe de développement",
    },
    {
      question: "Quelle est la durée de conservation de vos produits ?",
      answer: `Nos produits sont des produits artisanaux, sans conservateurs ni colorants. Certains produits sont à conserver au frais après ouverture. Au dos de chaque étiquette, vous trouverez la date de fabrication et la date de péremption.`,
      author: "Répondu par Guimety Corinne",
    },
  ];

  // 🌎 FAQ en Anglais 🌎
  const faqsEn = [
    {
      question: "Are your products artisanal and local?",
      answer: `We are processors and buy our quinces from local producers in Vaucluse and Drôme Provençale, who cultivate high-quality quinces. However, we also have our own quince trees, allowing us to produce some of our sweet products.`,
      author: "Answered by Guimety Corinne",
    },
    {
      question: "Do you have a physical store?",
      answer: `No, our products are available in fine grocery stores, wine cellars, restaurants, and supermarkets. You can find the complete list on the "Sales Points" page of our website.`,
      author: "Answered by the Au Coing Du Ventoux team",
    },
    {
      question: "Do you offer online sales?",
      answer: `For now, our website is mainly for presenting our products and company. An order form is available once you fill your cart and validate your email. In the near future, we plan to introduce an online sales system.`,
      author: "Answered by the development team",
    },
    {
      question: "What is the shelf life of your products?",
      answer: `Our products are artisanal, with no preservatives or colorants. Some products must be kept refrigerated after opening. You can find the manufacturing and expiration dates on the back of each label.`,
      author: "Answered by Guimety Corinne",
    },
  ];

  const faqs = i18n.language === "fr" ? faqsFr : faqsEn;

  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: '#FBF7EF' }}>
      <div className="max-w-3xl mx-auto px-6">
        {/* En-tête */}
        <div className="text-center mb-12">
          <span className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-800">
            FAQ
          </span>
          <h2 className="font-sans mt-4 text-3xl md:text-4xl text-stone-800">
            {t("faq.title")}
          </h2>
        </div>

        {/* Accordéon */}
        <div className="space-y-4">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`rounded-2xl border transition-colors duration-300 ${
                  isOpen ? 'bg-white shadow-md' : 'bg-white/60 hover:bg-white'
                }`}
                style={{ borderColor: isOpen ? 'rgba(217,166,65,0.5)' : 'rgba(120,90,40,0.15)' }}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full text-left px-6 py-5 flex justify-between items-center gap-4"
                  aria-expanded={isOpen}
                >
                  <span className="font-sans text-lg md:text-xl text-stone-800">
                    {faq.question}
                  </span>
                  {/* Chevron qui pivote */}
                  <span
                    className="flex-shrink-0 transition-transform duration-300"
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', color: '#D9A641' }}
                    aria-hidden="true"
                  >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M5 7.5 L10 12.5 L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>

                {/* Contenu animé (grid-rows 0fr -> 1fr pour une ouverture fluide) */}
                <div
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-6 pb-6">
                      <p className="font-nunito text-stone-600 leading-relaxed whitespace-pre-line">
                        {faq.answer}
                      </p>
                      <p className="font-nunito text-sm mt-3 italic" style={{ color: '#9a7b3e' }}>
                        {faq.author}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;